import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut as firebaseSignOut,
  updateProfile
} from "firebase/auth";
import { auth } from "../firebase/firebase";
import axios from 'axios';

import { getApiBaseUrl } from '../config/api';

const API_BASE_URI = getApiBaseUrl();
const userAuthContext = createContext();

export function UserAuthContextProvider({ children }) {
  const [user, setUser] = useState(null);       // raw Firebase user
  const [userProfile, setUserProfile] = useState(null); // Firestore doc
  const [loading, setLoading] = useState(true);

  // Google sign-in function
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      console.log("Google sign-in successful:", result.user.uid);

      try {
        // Get ID token for backend
        const idToken = await result.user.getIdToken();

        // Send to backend to create/update user document
        const response = await axios.post(`${API_BASE_URI}/auth/google-auth`, {
          idToken
        });

        console.log("Backend user creation successful:", response.data);
      } catch (error) {
        console.error("Error creating user in backend:", error);
      }

      return result;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Phone sign-in setup
  const setUpRecaptcha = (number) => {
    try {
      const recaptchaVerifier = new RecaptchaVerifier(
          auth,
          'recaptcha-container',
          {
            size: 'invisible',
            callback: () => console.log('reCAPTCHA solved.'),
            'expired-callback': () => console.error('reCAPTCHA expired.'),
          }
      );
      recaptchaVerifier.render();
      return signInWithPhoneNumber(auth, number, recaptchaVerifier);
    } catch (err) {
      console.error('Error during reCAPTCHA setup:', err.message);
      throw err;
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserProfile(null);
      return true;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Fetch user profile from Firestore via API
  const fetchUserProfile = async (currentUser) => {
    try {
      console.log("Fetching user profile for:", currentUser.uid);
      const token = await currentUser.getIdToken();
      const response = await axios.get(`${API_BASE_URI}/users/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log("User profile:", response.data.user);
      
      // If no user profile found but we have a currentUser, create a minimal profile
      if (!response.data.user && currentUser) {
        console.log('No user profile found, creating minimal profile');
        return {
          uid: currentUser.uid,
          email: currentUser.email || '',
          displayName: currentUser.displayName || currentUser.phoneNumber || 'User',
          phoneNumber: currentUser.phoneNumber || '',
          photoURL: currentUser.photoURL || '',
          provider: currentUser.phoneNumber ? 'phone' : 'unknown',
          role: 'user',
          createdAt: new Date().toISOString(),
          isActive: true
        };
      }
      
      return response.data.user;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      
      // If API fails but we have a currentUser, create a minimal profile to prevent errors
      if (currentUser) {
        console.log('API failed, creating fallback minimal profile');
        return {
          uid: currentUser.uid,
          email: currentUser.email || '',
          displayName: currentUser.displayName || currentUser.phoneNumber || 'User',
          phoneNumber: currentUser.phoneNumber || '',
          photoURL: currentUser.photoURL || '',
          provider: currentUser.phoneNumber ? 'phone' : 'unknown',
          role: 'user',
          createdAt: new Date().toISOString(),
          isActive: true
        };
      }
      
      return null;
    }
  };

  // Update user profile
  const updateUserProfile = async (profileData) => {
    try {
      if (!user) {
        throw new Error('User is not authenticated');
      }

      console.log("Updating user profile:", profileData);
      const token = await user.getIdToken();
      
      // Update in Firestore via API
      const response = await axios.put(
        `${API_BASE_URI}/users/profile`, 
        profileData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log("Profile updated in backend:", response.data);

      // Update Firebase Auth user profile if displayName is provided
      if (profileData.displayName && user.displayName !== profileData.displayName) {
        await updateProfile(user, {
          displayName: profileData.displayName
          // Note: photoURL can't be easily updated for Google/Phone auth users
        });
        console.log("Firebase Auth profile updated");
      }

      // Refresh user profile
      const updatedProfile = await fetchUserProfile(user);
      setUserProfile(updatedProfile);

      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("Auth state changed:", currentUser?.uid);
      setUser(currentUser);

      if (!currentUser) {
        // Not logged in
        setUserProfile(null);
        setLoading(false);
        return;
      }

      try {
        // For phone authentication, create user document if it doesn't exist
        if (currentUser.phoneNumber && !currentUser.email) {
          console.log('Phone authentication detected, ensuring user document exists');
          const token = await currentUser.getIdToken();
          
          try {
            // Try to create/update user document for phone users
            await axios.post(`${API_BASE_URI}/auth/ensure-user-document`, {
              uid: currentUser.uid,
              phoneNumber: currentUser.phoneNumber,
              displayName: currentUser.displayName || `User ${currentUser.phoneNumber?.slice(-4) || 'Unknown'}`
            }, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
            console.log('User document ensured for phone auth user');
          } catch (createError) {
            console.error('Error ensuring user document:', createError);
            // Continue anyway, we'll still try to fetch the profile
          }
        }

        // Get user profile from API
        const profile = await fetchUserProfile(currentUser);
        setUserProfile(profile);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
      <userAuthContext.Provider
          value={{
            user,
            userProfile,
            setUpRecaptcha,
            signInWithGoogle,
            signOut,
            updateUserProfile,
            loading
          }}
      >
        {children}
      </userAuthContext.Provider>
  );
}

export function useUserAuth() {
  return useContext(userAuthContext);
}